import type {
  CreatePromotionRecordDTO,
  PromotionRecordDetailDTO,
  UpdatePromotionRecordDTO,
} from "@/modules/hr/promotion/domain/types";
import { config } from "@/shared/lib/config";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  type LookupItem,
} from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  confirm,
  FormInput,
  FormRoot,
  FormTextarea,
  Input,
  Switch,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, Target, XCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import * as RHF from "react-hook-form";
import { z } from "zod";
import { promotionApi } from "../api/client";
import {
  useEmployeeCurrentPositionQuery,
  useValidToPositionsQuery,
} from "../api/queries";
import { promotionRecordFormSamples } from "./promotionRecordFormSamples";

const PromotionRecordFormSchema = z.object({
  employeeId: z.string().min(1, "ກະລຸນາເລືອກພະນັກງານ"),
  fromPositionId: z.string(), // empty string = no position (clearable combobox)
  toPositionId: z.string().min(1, "ກະລຸນາເລືອກຕຳແໜ່ງໃໝ່"),
  fromLevelId: z.string(),
  toLevelId: z.string(),
  previousSalary: z.number().optional(),
  newSalary: z.number().optional(),
  promotedAt: z.string().min(1, "ກະລຸນາເລືອກວັນທີເລື່ອນຕຳແໜ່ງ"),
  reason: z.string(),
  applyToEmployee: z.boolean(),
  enforceRule: z.boolean(),
});

type Option = { value: string; label: string };

type FormValues = {
  employeeId: string;
  fromPositionId: string;
  toPositionId: string;
  fromLevelId: string;
  toLevelId: string;
  previousSalary: number | undefined;
  newSalary: number | undefined;
  promotedAt: string;
  reason: string;
  applyToEmployee: boolean;
  enforceRule: boolean;
};

const POSITIONS_LOOKUP_URL = `${config.apiUrl}/positions/lookup`;
const USERS_LOOKUP_URL = `${config.apiUrl}/users/lookup`;

type Props = {
  onCancel: () => void;
  isSubmitting?: boolean;
  levelOptions: Option[];
} & (
  | {
      mode: "create";
      onSubmit: (values: CreatePromotionRecordDTO) => void;
    }
  | {
      mode: "edit";
      initialValues: PromotionRecordDetailDTO;
      onUpdate: (values: UpdatePromotionRecordDTO) => void;
    }
  | {
      mode: "view";
      initialValues: PromotionRecordDetailDTO;
    }
);

const NONE = "__none__";

function formatSalaryDisplay(value: number | undefined): string {
  if (value == null || Number.isNaN(value)) return "";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function parseSalaryInput(s: string): number | undefined {
  const cleaned = String(s).trim().replace(/,/g, "");
  if (cleaned === "") return undefined;
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

const isDev =
  typeof import.meta !== "undefined" &&
  (import.meta.hot !== undefined || import.meta.env?.MODE === "development");

export function PromotionRecordForm(props: Props) {
  const { onCancel, isSubmitting, levelOptions } = props;
  const isEdit = props.mode === "edit";
  const isView = props.mode === "view";
  const isReadOnly = isEdit || isView;

  const initialData =
    (isEdit || isView) && "initialValues" in props ? props.initialValues : null;
  const defaultValues = useMemo<FormValues>(() => {
    if (initialData) {
      const r = initialData;
      return {
        employeeId: r.employeeId,
        fromPositionId: r.fromPositionId ?? "",
        toPositionId: r.toPositionId,
        fromLevelId: r.fromLevelId ?? NONE,
        toLevelId: r.toLevelId ?? NONE,
        previousSalary: r.previousSalary ?? undefined,
        newSalary: r.newSalary ?? undefined,
        promotedAt: r.promotedAt.slice(0, 10),
        reason: r.reason ?? "",
        applyToEmployee: false,
        enforceRule: false,
      };
    }
    return {
      employeeId: "",
      fromPositionId: "",
      toPositionId: "",
      fromLevelId: NONE,
      toLevelId: NONE,
      previousSalary: undefined,
      newSalary: undefined,
      promotedAt: new Date().toISOString().slice(0, 10),
      reason: "",
      applyToEmployee: false,
      enforceRule: false,
    };
  }, [initialData]);

  const methods = RHF.useForm<FormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference
    resolver: zodResolver(PromotionRecordFormSchema) as any,
    defaultValues,
    values: initialData
      ? {
          employeeId: initialData.employeeId,
          fromPositionId: initialData.fromPositionId ?? "",
          toPositionId: initialData.toPositionId,
          fromLevelId: initialData.fromLevelId ?? NONE,
          toLevelId: initialData.toLevelId ?? NONE,
          previousSalary: initialData.previousSalary ?? undefined,
          newSalary: initialData.newSalary ?? undefined,
          promotedAt: initialData.promotedAt.slice(0, 10),
          reason: initialData.reason ?? "",
          applyToEmployee: false,
          enforceRule: false,
        }
      : undefined,
  });
  const [sampleIndex, setSampleIndex] = useState(0);
  const employeeId = methods.watch("employeeId");
  const fromPositionId = methods.watch("fromPositionId");
  const toPositionId = methods.watch("toPositionId");
  const promotedAt = methods.watch("promotedAt");
  const { data: currentPosition } = useEmployeeCurrentPositionQuery(
    employeeId?.trim() ? employeeId : undefined,
  );
  const validFromPositionId = fromPositionId?.trim()
    ? fromPositionId
    : undefined;
  const { data: validToPositions = [] } =
    useValidToPositionsQuery(validFromPositionId);

  const { data: kpiCheckPreview } = useQuery({
    queryKey: [
      "promotion-kpi-check-preview",
      employeeId?.trim(),
      toPositionId?.trim(),
      promotedAt,
    ] as const,
    queryFn: () =>
      promotionApi.getKpiCheckPreview({
        employeeId: (employeeId ?? "").trim(),
        positionId: toPositionId?.trim() || undefined,
        referenceDate: promotedAt || undefined,
      }),
    enabled:
      !isEdit &&
      !isView &&
      !!employeeId?.trim() &&
      employeeId.trim().length > 0,
  });

  const kpiBlockSubmit =
    props.mode === "create" &&
    kpiCheckPreview?.kpiCheckMode === "mandatory" &&
    kpiCheckPreview?.kpiCheck != null &&
    !kpiCheckPreview.kpiCheck.passed;

  // Auto-fill "from position" when employee has a current assignment
  useEffect(() => {
    if (!employeeId?.trim()) {
      methods.setValue("fromPositionId", "");
      return;
    }
    if (currentPosition?.positionId) {
      methods.setValue("fromPositionId", currentPosition.positionId);
    } else if (currentPosition && !currentPosition.positionId) {
      methods.setValue("fromPositionId", "");
    }
  }, [employeeId, currentPosition?.positionId, methods]);

  // When from position becomes NONE, turn off enforceRule (backend requires fromPositionId for rule validation)
  useEffect(() => {
    if (!validFromPositionId && methods.getValues("enforceRule")) {
      methods.setValue("enforceRule", false);
    }
  }, [validFromPositionId, methods]);

  // When from position changes: if we have valid to options, reset toPositionId if current value is not valid
  useEffect(() => {
    if (validFromPositionId && validToPositions.length > 0) {
      const currentTo = methods.getValues("toPositionId");
      const validIds = new Set(validToPositions.map((p) => p.value));
      if (currentTo && !validIds.has(currentTo)) {
        methods.setValue("toPositionId", "");
      }
    }
  }, [validFromPositionId, validToPositions, methods]);

  const handleAutoFill = () => {
    const sample = promotionRecordFormSamples[sampleIndex];
    if (sample) {
      methods.setValue("previousSalary", sample.previousSalary);
      methods.setValue("newSalary", sample.newSalary);
      methods.setValue("promotedAt", sample.promotedAt);
      methods.setValue("reason", sample.reason);
      methods.setValue("applyToEmployee", sample.applyToEmployee);
      methods.setValue("enforceRule", sample.enforceRule);
      setSampleIndex((prev) => (prev + 1) % promotionRecordFormSamples.length);
      toast.success("ເພີ່ມຂໍ້ມູນຕົວຢ່າງແລ້ວ. ກະລຸນາເລືອກພະນັກງານ ແລະ ຕຳແໜ່ງ.");
    }
  };

  const submit = async (values: FormValues) => {
    if (isView) return;
    if (isEdit && "onUpdate" in props) {
      props.onUpdate({
        fromPositionId: !values.fromPositionId?.trim()
          ? null
          : values.fromPositionId,
        toPositionId: values.toPositionId,
        fromLevelId: values.fromLevelId === NONE ? null : values.fromLevelId,
        toLevelId: values.toLevelId === NONE ? null : values.toLevelId,
        previousSalary: values.previousSalary ?? null,
        newSalary: values.newSalary ?? null,
        promotedAt: values.promotedAt,
        reason: values.reason.trim() || null,
      });
      return;
    }

    if (values.applyToEmployee && "onSubmit" in props) {
      const ok = await confirm({
        title: "ຢືນຢັນນຳໃຊ້ການເລື່ອນຂັ້ນ",
        description: "ຈະອັບເດດການກຳນົດຕຳແໜ່ງຫຼັກຂອງພະນັກງານທັນທີ",
        actionText: "ຢືນຢັນ",
      });
      if (!ok) return;
    }

    if ("onSubmit" in props) {
      props.onSubmit({
        employeeId: values.employeeId,
        fromPositionId: !values.fromPositionId?.trim()
          ? null
          : values.fromPositionId,
        toPositionId: values.toPositionId,
        fromLevelId: values.fromLevelId === NONE ? null : values.fromLevelId,
        toLevelId: values.toLevelId === NONE ? null : values.toLevelId,
        previousSalary: values.previousSalary ?? null,
        newSalary: values.newSalary ?? null,
        promotedAt: values.promotedAt,
        reason: values.reason.trim() || null,
        applyToEmployee: values.applyToEmployee,
        enforceRule: values.enforceRule,
      });
    }
  };

  const levelSelect = [{ value: NONE, label: "-" }, ...levelOptions];
  const currentToId = methods.watch("toPositionId");
  const needHydrateToPosition =
    validFromPositionId &&
    !!currentToId &&
    !validToPositions.some((p) => p.value === currentToId);
  const { data: hydratedToPosition } = useQuery({
    queryKey: ["position-lookup", currentToId] as const,
    queryFn: () =>
      hydrateLookupItem<LookupItem>(POSITIONS_LOOKUP_URL, currentToId),
    enabled: !!needHydrateToPosition && !!currentToId,
  });
  const toPositionOptionsWhenRestricted = useMemo(() => {
    if (!validFromPositionId || validToPositions.length === 0) return [];
    const base = validToPositions;
    if (
      hydratedToPosition &&
      currentToId &&
      !base.some((o) => o.value === currentToId)
    ) {
      return [
        { value: hydratedToPosition.id, label: hydratedToPosition.name },
        ...base,
      ];
    }
    return base;
  }, [validFromPositionId, validToPositions, hydratedToPosition, currentToId]);

  return (
    <FormRoot<FormValues>
      methods={methods}
      onSubmit={submit}
      className="space-y-6"
    >
      <Card>
        <CardHeader>
          <CardTitle>
            {isView
              ? "ລາຍລະອຽດບັນທຶກການເລື່ອນຕຳແໜ່ງ"
              : isEdit
                ? "ແກ້ໄຂບັນທຶກການເລື່ອນຕຳແໜ່ງ"
                : "ສ້າງບັນທຶກການເລື່ອນຕຳແໜ່ງ"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormInfiniteCombobox<LookupItem>
            name="employeeId"
            label="ພະນັກງານ"
            queryKey={["users-lookup-promotion"]}
            queryFn={(args) =>
              fetchLookupForInfinite<LookupItem>(USERS_LOOKUP_URL, args)
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<LookupItem>(USERS_LOOKUP_URL, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກພະນັກງານ"
            requiredMark
            disabled={isReadOnly}
            className="w-full"
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormInfiniteCombobox<LookupItem>
              name="fromPositionId"
              label="ຕຳແໜ່ງເກົ່າ"
              queryKey={["positions-lookup"]}
              queryFn={(args) =>
                fetchLookupForInfinite<LookupItem>(POSITIONS_LOOKUP_URL, args)
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem<LookupItem>(POSITIONS_LOOKUP_URL, id)
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              placeholder="-"
              clearable
              disabled={isReadOnly}
              className="w-full"
            />
            {validFromPositionId && validToPositions.length > 0 ? (
              <RHF.Controller
                name="toPositionId"
                control={methods.control}
                render={({ field }) => (
                  <div className="space-y-2">
                    <label
                      htmlFor="promotion-record-to-position"
                      className="font-medium text-sm"
                    >
                      ຕຳແໜ່ງໃໝ່
                    </label>
                    <SimpleSelect
                      id="promotion-record-to-position"
                      options={toPositionOptionsWhenRestricted}
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder="ເລືອກຕຳແໜ່ງຖັດໄປທີ່ຖືກຕ້ອງຕາມກົດ"
                      className="w-full"
                      disabled={isReadOnly}
                    />
                  </div>
                )}
              />
            ) : (
              <FormInfiniteCombobox<LookupItem>
                name="toPositionId"
                label="ຕຳແໜ່ງໃໝ່"
                queryKey={["positions-lookup-to"]}
                queryFn={(args) =>
                  fetchLookupForInfinite<LookupItem>(POSITIONS_LOOKUP_URL, args)
                }
                preloadQueryFn={(id) =>
                  hydrateLookupItem<LookupItem>(POSITIONS_LOOKUP_URL, id)
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                placeholder={
                  validFromPositionId ? "ບໍ່ມີກົດເສັ້ນທາງສຳລັບຕຳແໜ່ງເກົ່ານີ້" : "ເລືອກຕຳແໜ່ງ"
                }
                requiredMark
                disabled={
                  isReadOnly ||
                  (!!validFromPositionId && validToPositions.length === 0)
                }
                className="w-full"
              />
            )}
            {validFromPositionId && validToPositions.length === 0 && (
              <p className="col-span-2 text-muted-foreground text-xs">
                ບໍ່ມີກົດເສັ້ນທາງສຳລັບຕຳແໜ່ງເກົ່ານີ້
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <RHF.Controller
              name="fromLevelId"
              control={methods.control}
              render={({ field }) => (
                <div className="space-y-2">
                  <label
                    htmlFor="promotion-record-from-level"
                    className="font-medium text-sm"
                  >
                    ລະດັບເກົ່າ
                  </label>
                  <SimpleSelect
                    id="promotion-record-from-level"
                    options={levelSelect}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="-"
                    className="w-full"
                    disabled={isReadOnly}
                  />
                </div>
              )}
            />
            <RHF.Controller
              name="toLevelId"
              control={methods.control}
              render={({ field }) => (
                <div className="space-y-2">
                  <label
                    htmlFor="promotion-record-to-level"
                    className="font-medium text-sm"
                  >
                    ລະດັບໃໝ່
                  </label>
                  <SimpleSelect
                    id="promotion-record-to-level"
                    options={levelSelect}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="-"
                    className="w-full"
                    disabled={isReadOnly}
                  />
                </div>
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <RHF.Controller
              name="previousSalary"
              control={methods.control}
              render={({ field }) => (
                <div className="space-y-2">
                  <label
                    htmlFor="promotion-record-previous-salary"
                    className="font-medium text-sm"
                  >
                    ເງິນເດືອນເກົ່າ
                  </label>
                  <Input
                    id="promotion-record-previous-salary"
                    type="text"
                    inputMode="numeric"
                    placeholder="0"
                    value={formatSalaryDisplay(field.value)}
                    onChange={(e) =>
                      field.onChange(parseSalaryInput(e.target.value))
                    }
                    onBlur={field.onBlur}
                    className="tabular-nums"
                    disabled={isReadOnly}
                  />
                </div>
              )}
            />
            <RHF.Controller
              name="newSalary"
              control={methods.control}
              render={({ field }) => (
                <div className="space-y-2">
                  <label
                    htmlFor="promotion-record-new-salary"
                    className="font-medium text-sm"
                  >
                    ເງິນເດືອນໃໝ່
                  </label>
                  <Input
                    id="promotion-record-new-salary"
                    type="text"
                    inputMode="numeric"
                    placeholder="0"
                    value={formatSalaryDisplay(field.value)}
                    onChange={(e) =>
                      field.onChange(parseSalaryInput(e.target.value))
                    }
                    onBlur={field.onBlur}
                    className="tabular-nums"
                    disabled={isReadOnly}
                  />
                </div>
              )}
            />
          </div>

          <FormInput
            name="promotedAt"
            label="ວັນທີເລື່ອນຂັ້ນ"
            type="date"
            requiredMark
            disabled={isReadOnly}
          />
          <FormTextarea
            name="reason"
            label="ເຫດຜົນ"
            rows={3}
            disabled={isReadOnly}
          />

          {!isEdit && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <p className="font-medium text-sm">Apply to employee</p>
                  <p className="text-muted-foreground text-xs">
                    ອັບເດດການກຳນົດຕຳແໜ່ງປັດຈຸບັນ
                  </p>
                </div>
                <Switch
                  checked={methods.watch("applyToEmployee")}
                  onCheckedChange={(v) =>
                    methods.setValue("applyToEmployee", v)
                  }
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <p className="font-medium text-sm">Enforce path rule</p>
                  <p className="text-muted-foreground text-xs">
                    {validFromPositionId
                      ? "ກວດ rule ກ່ອນບັນທຶກ (ຕ້ອງເລືອກຕຳແໜ່ງເກົ່າ)"
                      : "ກວດ rule ກ່ອນບັນທຶກ (ຕ້ອງເລືອກຕຳແໜ່ງເກົ່າກ່ອນ)"}
                  </p>
                </div>
                <Switch
                  checked={methods.watch("enforceRule")}
                  onCheckedChange={(v) => methods.setValue("enforceRule", v)}
                  disabled={!validFromPositionId}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {!isEdit && !isView && kpiCheckPreview?.kpiCheck != null && (
        <Card>
          <CardContent>
            <div className="flex flex-wrap items-center gap-2 font-medium text-sm">
              <Target className="h-4 w-4 shrink-0 text-muted-foreground" />
              ສະຖານະ KPI (ໄລຍະລ່າສຸດ)
              {kpiCheckPreview.kpiCheck.passed ? (
                <Badge variant="default" className="bg-emerald-600">
                  ຜ່ານທຸກລາຍການ
                </Badge>
              ) : (
                <Badge variant="destructive" className="gap-1">
                  <XCircle className="h-3 w-3" />
                  ຍັງບໍ່ຜ່ານ
                </Badge>
              )}
              {kpiCheckPreview.kpiCheckMode === "mandatory" &&
                !kpiCheckPreview.kpiCheck.passed && (
                  <span className="text-muted-foreground text-xs">
                    (ຕ້ອງຜ່ານທຸກລາຍການຈຶ່ງບັນທຶກໄດ້)
                  </span>
                )}
            </div>
            {kpiCheckPreview.kpiCheck.details.length > 0 && (
              <ul className="mt-3 space-y-1 border-t pt-3 text-muted-foreground text-xs">
                {kpiCheckPreview.kpiCheck.details.map((d) => (
                  <li
                    key={d.assignmentId}
                    className="flex flex-wrap items-center gap-2"
                  >
                    <span className="font-medium text-foreground">
                      {d.kpiName}
                    </span>
                    <span>
                      {d.periodYear}
                      {d.periodMonth != null ? `/${d.periodMonth}` : ""}
                      {d.periodQuarter != null ? ` Q${d.periodQuarter}` : ""}
                    </span>
                    <Badge
                      variant={
                        d.status === "pass"
                          ? "default"
                          : d.status === "fail"
                            ? "destructive"
                            : "secondary"
                      }
                      className="text-xs"
                    >
                      {d.status === "pass"
                        ? "ຜ່ານ"
                        : d.status === "fail"
                          ? "ບໍ່ຜ່ານ"
                          : d.status === "pending"
                            ? "ລໍຖ້າ"
                            : "ບໍ່ມີຜົນ"}
                    </Badge>
                    {d.actualValue != null && (
                      <span>
                        ຜົນ: {d.actualValue}
                        {d.targetValue != null
                          ? ` / ເປົ້າໝາຍ: ${d.targetValue}`
                          : ""}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-4 border-t bg-background/95 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {isView ? "ກັບຄືນ" : "ຍົກເລີກ"}
          </Button>
          {!isEdit &&
            !isView &&
            isDev &&
            promotionRecordFormSamples.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAutoFill}
                disabled={isSubmitting}
                className="gap-1.5"
              >
                <Sparkles className="h-4 w-4" />
                Auto Fill (Sample {sampleIndex + 1}/
                {promotionRecordFormSamples.length})
              </Button>
            )}
        </div>
        {!isView && (
          <Button
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting || kpiBlockSubmit}
            title={kpiBlockSubmit ? "ຕ້ອງຜ່ານ KPI ທຸກລາຍການກ່ອນບັນທຶກ" : undefined}
          >
            {isEdit ? "ບັນທຶກການແກ້ໄຂ" : "ບັນທຶກ"}
          </Button>
        )}
      </div>
    </FormRoot>
  );
}
